<template>
    <TransitionRoot
        as="template"
        :show="_open"
    >
        <Dialog
            as="div"
            class="relative z-10"
            @close="close"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
            </TransitionChild>

            <div class="fixed z-10 inset-0 overflow-y-auto">
                <div class="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full sm:p-6">
                            <div class="sm:flex sm:items-start">
                                <div class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <XCircleIcon
                                        class="h-6 w-6 text-red-600"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                    <DialogTitle
                                        as="h3"
                                        class="text-lg leading-6 font-medium text-gray-900"
                                    >
                                        {{ _title ?? "Error" }}
                                    </DialogTitle>
                                    <div class="mt-2 w-full">
                                        {{ _messageContents }}
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-xs px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    @click="close"
                                >
                                    {{ _closeButtonText ?? "Close" }}
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
import { XCircleIcon } from '@heroicons/vue/24/solid';
import { Ref, ref } from 'vue';
import { TransitionRoot, TransitionChild, DialogPanel, DialogTitle, Dialog } from '@headlessui/vue';
import { ModalConfig } from '@/stores/modals';

export interface ErrorConfig extends ModalConfig {
    closeButtonText: string | null;
}

export interface ErrorModal {
    show: (message: string | ErrorConfig) => Promise<void>;
}

const _open = ref(false);
const _close = ref(false);
const _messageContents: Ref<string | null> = ref(null);
const _title: Ref<string | null> = ref(null);
const _closeButtonText: Ref<string | null> = ref(null);

const timeout = async (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

async function show(message: string | ErrorConfig) {
    _messageContents.value = null;
    _title.value = null;
    _closeButtonText.value = null;
    if (typeof message == "string") {
        _messageContents.value = message;
    }
    else {
        _messageContents.value = message.message
        _title.value = message.title;
        _closeButtonText.value = message.closeButtonText;
    }
    _open.value = true;
    while (_close.value === false) await timeout(50);
    _close.value = false;
    _open.value = false;
}

function close() {
    _close.value = true;
}

defineExpose({
    show
})
</script>